import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Trophy } from "lucide-react";

import { audioService } from "@/core/audio/audioService";
import Confetti from "@/plugins/bingo/projector/effects/Confetti";
import Fireworks from "@/plugins/bingo/projector/effects/Fireworks";

/**
 * ConfirmedBingoOverlay — the signature Confirmed Bingo Celebration.
 *
 * HYBRID choreography (approved):
 *   0.00 → 0.30s  Silence · black void · dramatic breath after the
 *                 heartbeat cut. Nothing on screen.
 *   0.30 → 0.55s  Warm champagne gradient fades in (intimate).
 *                 Delicate string chord plays.
 *   0.55 → 1.60s  ELEGANT GOLD REVEAL — "Congratulations," then the
 *                 winner's name in a shimmering gold gradient. Intimate,
 *                 tender, name-first.
 *   1.60 → 2.40s  Stage badge ("FULL HOUSE WINNER") settles beneath.
 *                 Room stays intimate — still no confetti / brass.
 *   2.40s         BUILD → BURST. Brass fanfare fires. Confetti + fireworks
 *                 explode. Backdrop shifts from intimate warm to full stage.
 *                 Big "CONFIRMED BINGO!" headline rises above the name.
 *                 Trophy pops with glow pulse. Spotlight sweep.
 *   2.40 → 6.50s  Full celebration state. Confetti drifts. Spotlight sweeps.
 *                 Winner name kept center stage.
 *   6.50 → 8.00s  Wind-down. Confetti settles. Music fades. Backdrop dims.
 *   8.00s         Ready to dismiss (host clicks Continue OR auto-return).
 *
 * The overlay never mutates game state — the parent decides when to
 * dismiss it (auto-return timer + host "Continue" button).
 */
export default function ConfirmedBingoOverlay({ active, name, stageLabel }) {
  const displayName = (name || "").trim() || "Congratulations!";
  const [burst, setBurst] = useState(false);

  // Audio + celebration burst timeline
  useEffect(() => {
    if (!active) {
      setBurst(false);
      return;
    }
    const timers = [];
    // Intimate chord after the silence
    timers.push(setTimeout(() => audioService.playIntimateChord(), 300));
    // Brass fanfare when the celebration bursts
    timers.push(
      setTimeout(() => {
        setBurst(true);
        audioService.playCelebrationBrass();
      }, 2400),
    );
    return () => timers.forEach(clearTimeout);
  }, [active]);

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          data-testid="confirmed-bingo-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[80] flex items-center justify-center overflow-hidden"
          style={{ background: "rgba(0, 0, 0, 1)" }}
        >
          {/* Intimate warm champagne backdrop — fades in after the silence,
              intensifies on the burst. */}
          <motion.div
            aria-hidden
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{
              opacity: burst ? 1 : 0.6,
            }}
            transition={{ delay: 0.3, duration: 0.7, ease: "easeOut" }}
            style={{
              background: burst
                ? "radial-gradient(ellipse 65% 55% at 50% 45%, rgba(251,191,36,0.55) 0%, rgba(217,119,6,0.35) 35%, rgba(0,0,0,0.9) 75%, #000 100%)"
                : "radial-gradient(ellipse 45% 40% at 50% 50%, rgba(251,191,36,0.35) 0%, rgba(120,53,15,0.25) 40%, rgba(0,0,0,0.95) 80%, #000 100%)",
              transition: "background 1.2s ease-out",
            }}
          />

          {/* Spotlight sweep — only during burst */}
          <AnimatePresence>
            {burst && <SpotlightSweep />}
          </AnimatePresence>

          {/* Confetti + Fireworks — only on burst */}
          {burst && (
            <>
              <Confetti count={140} />
              <Fireworks burstCount={5} />
            </>
          )}

          {/* Center content */}
          <div className="relative z-10 flex flex-col items-center text-center px-8">
            {/* Big cinematic headline — appears on burst, above the name */}
            <AnimatePresence>
              {burst && (
                <motion.h1
                  data-testid="confirmed-bingo-title"
                  initial={{ opacity: 0, y: -30, scale: 0.85 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    type: "spring",
                    stiffness: 180,
                    damping: 20,
                  }}
                  className="ic-font-display font-black leading-none mb-8"
                  style={{
                    fontSize: "clamp(3rem, 8vw, 8rem)",
                    letterSpacing: "0.05em",
                    background:
                      "linear-gradient(180deg, #FEF3C7 0%, #FBBF24 45%, #D97706 100%)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    textShadow: "0 0 60px rgba(251,191,36,0.55)",
                    filter:
                      "drop-shadow(0 4px 18px rgba(217,119,6,0.55))",
                  }}
                >
                  CONFIRMED BINGO!
                </motion.h1>
              )}
            </AnimatePresence>

            {/* Intimate "Congratulations," line */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.7 }}
              className="ic-font-display italic tracking-[0.25em] uppercase"
              style={{
                fontSize: "clamp(0.9rem, 1.4vw, 1.4rem)",
                color: "rgba(254, 243, 199, 0.75)",
                textShadow: "0 0 12px rgba(251,191,36,0.35)",
              }}
            >
              Congratulations,
            </motion.div>

            {/* Winner name — elegant gold shimmer */}
            <motion.div
              data-testid="confirmed-bingo-name"
              initial={{ opacity: 0, y: 20, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                delay: 0.75,
                duration: 0.85,
                type: "spring",
                stiffness: 160,
                damping: 22,
              }}
              className="ic-font-display font-black mt-3"
              style={{
                fontSize: "clamp(3.2rem, 8vw, 8rem)",
                letterSpacing: "-0.01em",
                lineHeight: 1.05,
                background:
                  "linear-gradient(180deg, #FFFBEB 0%, #FDE68A 30%, #FBBF24 60%, #B45309 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
                textShadow: "0 0 40px rgba(251,191,36,0.35)",
                filter: "drop-shadow(0 6px 24px rgba(0,0,0,0.55))",
              }}
            >
              <ShimmerText>{displayName}</ShimmerText>
            </motion.div>

            {/* Stage badge — settles below name */}
            {stageLabel && (
              <motion.div
                data-testid="confirmed-bingo-stage-label"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.6, duration: 0.6 }}
                className="mt-6 inline-flex items-center gap-3 px-5 py-2 rounded-full"
                style={{
                  background: "rgba(120, 53, 15, 0.35)",
                  border: "1px solid rgba(251, 191, 36, 0.55)",
                  color: "#FDE68A",
                  boxShadow: "0 0 24px rgba(251,191,36,0.35)",
                }}
              >
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                <span className="text-xs sm:text-sm tracking-[0.4em] uppercase font-semibold">
                  {stageLabel}
                </span>
              </motion.div>
            )}

            {/* Trophy — appears on burst */}
            <AnimatePresence>
              {burst && (
                <motion.div
                  initial={{ scale: 0, rotate: -30, opacity: 0 }}
                  animate={{
                    scale: [0, 1.25, 1],
                    rotate: [-30, 8, 0],
                    opacity: 1,
                  }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.7, ease: "easeOut" }}
                  className="mt-10"
                >
                  <motion.div
                    animate={{
                      filter: [
                        "drop-shadow(0 0 24px rgba(251,191,36,0.55))",
                        "drop-shadow(0 0 60px rgba(251,191,36,0.85))",
                        "drop-shadow(0 0 24px rgba(251,191,36,0.55))",
                      ],
                    }}
                    transition={{
                      duration: 2.4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    <Trophy
                      style={{
                        width: "clamp(3.5rem, 5.5vw, 6rem)",
                        height: "clamp(3.5rem, 5.5vw, 6rem)",
                        color: "#FBBF24",
                      }}
                      strokeWidth={2}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------------- Sub-effects ---------------- */

function SpotlightSweep() {
  return (
    <motion.div
      aria-hidden
      className="absolute inset-0 pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.65, 0.65, 0] }}
      transition={{ duration: 4, times: [0, 0.15, 0.85, 1] }}
      style={{
        background:
          "conic-gradient(from 0deg at 50% -20%, transparent 0deg, rgba(254,243,199,0.35) 20deg, transparent 40deg, transparent 320deg, rgba(254,243,199,0.35) 340deg, transparent 360deg)",
        mixBlendMode: "screen",
      }}
    />
  );
}

function ShimmerText({ children }) {
  // Subtle animated shimmer sweep across the gradient text.
  return (
    <motion.span
      style={{
        display: "inline-block",
        backgroundImage:
          "linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.9) 45%, transparent 60%)",
        backgroundClip: "text",
        WebkitBackgroundClip: "text",
        backgroundSize: "200% 100%",
      }}
      animate={{ backgroundPosition: ["-40% 0%", "140% 0%"] }}
      transition={{
        duration: 3.6,
        repeat: Infinity,
        ease: "easeInOut",
        delay: 1.5,
      }}
    >
      {children}
    </motion.span>
  );
}
